import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { MODAL_SIZE } from '@CommonConstants';
import themes, { THEME_TYPES } from '@Styles/Themes';

import { postProfileImageId } from '@Services/User/Request';

import { ReactComponent as CheckSVG } from '@Assets/Svgs/ic_check.svg';
import Modal from '@Components/Common/Modal';
import Button from '@Components/Common/Button';

import { profileImageList } from './ProfileImageList';

const ProfileImageModal = ({ isProfileImageModalVisible, setAuthor, handleProfileImageModalVisible }) => {
  const [selectedProfileImageId, setSelectedProfileImageId] = useState(null);
  const theme = themes[THEME_TYPES.LIGHT];

  const handleProfileImageClick = useCallback(
    id => {
      setSelectedProfileImageId(id);
    },
    [selectedProfileImageId],
  );

  const handleProfileImageSetClick = useCallback(async () => {
    await postProfileImageId(selectedProfileImageId);
    await setAuthor();
    handleProfileImageModalVisible();
  }, [selectedProfileImageId]);

  return (
    <Modal
      visible={isProfileImageModalVisible}
      size={MODAL_SIZE.BIG}
      handleCloseButtonClick={handleProfileImageModalVisible}
    >
      <InnerContainer>
        {profileImageList.map(({ id, imageURL }) => {
          const isSelectedProfileImage = id === selectedProfileImageId;

          return (
            <ProfileImageContainer key={id} onClick={() => handleProfileImageClick(id)}>
              <ProfileImage src={imageURL} />
              {isSelectedProfileImage && <CheckIcon />}
            </ProfileImageContainer>
          );
        })}
      </InnerContainer>

      <SelectProfileImageButtonContainer>
        <Button
          text="취소"
          backgroundColor={theme.colors.ui_2}
          textColor={theme.colors.ui_on_1}
          onClick={handleProfileImageModalVisible}
        />
        <Button text="적용" disabled={selectedProfileImageId === null} onClick={handleProfileImageSetClick} />
      </SelectProfileImageButtonContainer>
    </Modal>
  );
};

ProfileImageModal.propTypes = {
  isProfileImageModalVisible: PropTypes.bool.isRequired,
  setAuthor: PropTypes.func.isRequired,
  handleProfileImageModalVisible: PropTypes.func.isRequired,
};

const InnerContainer = styled.div`
  width: 100%;
  max-height: 488px;

  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px 43px;

  padding: 0px 20px 40px 20px;
  margin-top: -16px;

  overflow-y: scroll;
`;

const ProfileImageContainer = styled.div`
  position: relative;

  width: 100px;
  height: 100px;

  cursor: pointer;
`;

const ProfileImage = styled.img.attrs(({ src }) => ({
  src,
}))`
  width: 100%;
  height: 100%;

  border-radius: 72px;
  object-fit: cover;
`;

const CheckIcon = styled(CheckSVG)`
  position: absolute;
  top: 0;
  left: 0;

  width: 24px;
  height: 24px;
`;

const SelectProfileImageButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  position: absolute;
  bottom: 0;

  width: 100%;
  height: 60px;

  gap: 0px 8px;
  padding: 8px 20px 20px 20px;
  border-radius: 0px 0px 14px 14px;
`;

export default ProfileImageModal;
